import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Attendance : famille "présence (émargement)".
 *
 * Constat individuel horodaté de présence, d'absence, de retard ou de no-show
 * d'une personne à une occurrence donnée, signable pour valoir preuve.
 * Construite sur le modèle "famille + type" de la bible (patron : `asset`).
 *
 * Le socle ne connaît pas de constat concret (appel du cours de judo,
 * émargement d'un stagiaire, badge d'un abonné) : il connaît la famille
 * générique `attendance`. Le vocabulaire métier est porté par le champ `type`
 * (chaîne libre déclarée par l'app), et le constat lui-même (présent, absent,
 * retard, no-show) par `status`.
 *
 * Références vers d'autres familles = de simples ids (aucun import de schéma) :
 * `contactId` (la personne pointée) est le CŒUR du constat individuel, donc
 * requis. La cible est POLYMORPHE (`targetRef` de la fiche) : le constat vise
 * soit un `event`, soit un `booking`, soit un `shift` ; on modélise les trois
 * en ids optionnels (une seule cible à la fois, aucune exigée). Les liens
 * `registration`, `credit` et `policy` restent optionnels.
 *
 * Sécurité : l'accès réel dépend de la cible (pointage d'un shift salarié :
 * owner plus manager ; contact mineur : encadrants plus représentant légal via
 * relationship). En v0, la famille reste org-wide comme le reste du socle ; ce
 * cloisonnement fin viendra après. Aucun champ n'est donc retiré ici : la
 * sensibilité porte sur la ligne (qui peut la lire), pas sur une colonne.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille attendance (hors champs standard du socle). */
export const attendanceCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "periscolaire", "formation", "badge"). */
  type: z.string().min(1),
  /** Personne pointée (référence `contact`, cœur du constat individuel). */
  contactId: z.string().min(1),
  /** Cible polymorphe : occurrence `event` émargée, optionnel. */
  eventId: z.string().min(1).optional(),
  /** Cible polymorphe : rendez-vous `booking` honoré ou non, optionnel. */
  bookingId: z.string().min(1).optional(),
  /** Cible polymorphe : créneau de travail `shift` pointé, optionnel. */
  shiftId: z.string().min(1).optional(),
  /** Constat, chaîne libre pilotée par l'app (ex. "present", "absent", "retard", "no_show"). */
  status: z.string().min(1).optional(),
  /** Horodatage d'arrivée (check-in), optionnel. */
  checkInAt: z.date().optional(),
  /** Horodatage de départ (check-out), optionnel. */
  checkOutAt: z.date().optional(),
  /** Signataire de l'émargement (uid ou id qui valide le constat), optionnel. */
  signedBy: z.string().min(1).optional(),
  /** Inscription confrontée au réel (référence `registration`), optionnel. */
  registrationId: z.string().min(1).optional(),
  /** Carnet dont une séance est décomptée si présence (référence `credit`), optionnel. */
  creditId: z.string().min(1).optional(),
  /** Règle appliquée au no-show (référence `policy`), optionnel. */
  policyId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille attendance (champs standard + champs métier). */
export const AttendanceSchema = z.object({
  ...baseEntityFields,
  ...attendanceCoreFields,
});

/** Type d'une attendance. */
export type Attendance = z.infer<typeof AttendanceSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const AttendanceCreateInputSchema = z.object({
  ...attendanceCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'une attendance. */
export type AttendanceCreateInput = z.infer<typeof AttendanceCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const AttendanceUpdateInputSchema = z
  .object({ ...attendanceCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'une attendance. */
export type AttendanceUpdateInput = z.infer<typeof AttendanceUpdateInputSchema>;
